import React, { useState, useEffect } from 'react';
import {
  X,
  Package,
  Save,
  ToggleLeft,
  ToggleRight,
  PlusCircle,
} from 'lucide-react';
import { UnifiedPatient, PackageInfo, ValidityType, FinancialProfile } from '../../types/unified-financial';
import { PatientService } from '../../services/PatientService';
import { PackageService } from '../../services/PackageService';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-toastify';


interface PatientFinancialModalProps {
  patient: UnifiedPatient;
  isOpen: boolean;
  onClose: () => void;
  onUpdate: () => void;
}

const defaultPackageInfo = (financialProfileId: string = '', clientId: string = ''): PackageInfo => ({
  packageId: `pkg_${Date.now()}`,
  clientId,
  financialProfileId,
  packageName: '',
  packagePrice: 0,
  packageSessionsContracted: 8,
  packageSessionsUsed: 0,
  packageSessionsRemaining: 8,
  packageValidityType: '1_MONTH' as ValidityType,
  packageCustomValidityMonths: undefined,
  packagePaymentDate: new Date().toISOString().split('T')[0],
  packageRenewalDate: '',
  packageNotes: '',
  packageCreatedAt: new Date().toISOString(),
});

export default function PatientFinancialModal({ patient, isOpen, onClose, onUpdate }: PatientFinancialModalProps) {
  const { selectedClient } = useAuth();
  const [isNewPackage, setIsNewPackage] = useState(true);
  const [loading, setLoading] = useState(false);
  const [showAddCredits, setShowAddCredits] = useState(false);
  const [isAddingCredits, setIsAddingCredits] = useState(false);
  const [creditsForm, setCreditsForm] = useState({ sessions: 1, price: 0 });

  const handleAddCredits = async () => {
    if (creditsForm.sessions <= 0) {
      toast.error("O número de sessões a adicionar deve ser maior que zero.");
      return;
    }
    if (creditsForm.price < 0) {
      toast.error("O valor a ser cobrado não pode ser negativo.");
      return;
    }

    // FIX: Ler o packageId diretamente da prop 'patient' para evitar estado obsoleto.
    const packageId = patient.financialProfile.packageInfo?.packageId;

    if (!packageId) {
      toast.error("Erro: ID do pacote do paciente atual não foi encontrado.");
      return;
    }

    try {
      setIsAddingCredits(true);
      
      await PackageService.addCredits(
        packageId,
        creditsForm.sessions,
        creditsForm.price
      );

      toast.success(`${creditsForm.sessions} sessão(ões) adicionada(s) com sucesso! A cobrança foi adicionada à fatura do paciente.`);
      
      // Reseta o formulário e fecha a seção de adicionar créditos
      setShowAddCredits(false);
      setCreditsForm({ sessions: 1, price: 0 });

      // Chama a função onUpdate para recarregar os dados no dashboard principal
      onUpdate();

      // Fecha o modal principal
      onClose();

    } catch (error: any) {
      console.error("❌ Erro ao adicionar créditos:", error);
      toast.error(`Erro ao adicionar créditos: ${error.message}`);
    } finally {
      setIsAddingCredits(false);
    }
  };

  const [formData, setFormData] = useState(() => {
    const initialProfile = patient.financialProfile;
    return {
      active: initialProfile.active,
      hasPackage: initialProfile.hasPackage,
      individualSessionPrice: initialProfile.individualSessionPrice || 150,
      packageInfo: initialProfile.packageInfo || defaultPackageInfo(initialProfile.financialProfileId, initialProfile.clientId || selectedClient?.clientId || ''),
    };
  });

  useEffect(() => {
    if (isOpen) {
      const existingPackage = patient.financialProfile.packageInfo;
      const isTrulyNew = !existingPackage || !existingPackage.packageId || existingPackage.packageId.startsWith("pkg_");
      setIsNewPackage(isTrulyNew);

      setFormData({
        active: patient.financialProfile.active,
        hasPackage: patient.financialProfile.hasPackage,
        individualSessionPrice: patient.financialProfile.individualSessionPrice || 150,
        packageInfo: existingPackage || defaultPackageInfo(patient.financialProfile.financialProfileId, patient.financialProfile.clientId || selectedClient?.clientId || ''),
      });
    }
  }, [patient, isOpen]);

  const handleSave = async () => {
    try {
      setLoading(true);

      const clientId = patient.financialProfile.clientId || selectedClient?.clientId;
      if (!clientId) {
        toast.error("Erro de identificação do cliente. A operação foi cancelada.");
        setLoading(false);
        return;
      }

      const profileToUpdate: Partial<FinancialProfile> & { clientId: string } = {
        active: formData.active,
        hasPackage: formData.hasPackage,
        individualSessionPrice: formData.individualSessionPrice,
        clientId,
        packageInfo: formData.hasPackage ? {
          ...formData.packageInfo!,
          packageRenewalDate: calculateRenewalDate(),
        } : undefined,
      };

      await PatientService.updateFinancialProfile(patient.id, profileToUpdate);

      toast.success("Configuração financeira salva com sucesso!");
      onUpdate();
      onClose();

    } catch (error: any) {
      console.error("❌ Erro ao salvar:", error);
      toast.error(`Erro ao salvar: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const calculateRenewalDate = () => {
    if (formData.packageInfo?.packagePaymentDate && formData.packageInfo?.packageValidityType) {
      const paymentDate = new Date(formData.packageInfo.packagePaymentDate);
      const renewalDate = new Date(paymentDate);
      let monthsToAdd = 1;
      if (formData.packageInfo.packageValidityType === '2_MONTHS') monthsToAdd = 2;
      else if (formData.packageInfo.packageValidityType === '3_MONTHS') monthsToAdd = 3;
      else if (formData.packageInfo.packageValidityType === 'CUSTOM' && formData.packageInfo.packageCustomValidityMonths) {
        monthsToAdd = formData.packageInfo.packageCustomValidityMonths;
      }
      renewalDate.setMonth(renewalDate.getMonth() + monthsToAdd);
      return renewalDate.toISOString().split('T')[0];
    }
    return undefined;
  };

  if (!isOpen) return null;

  const packageInfo = formData.packageInfo || defaultPackageInfo();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-semibold">Configuração Financeira - {patient.personalData.name}</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-6 space-y-6">
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div>
              <h3 className="font-medium">Status do Paciente</h3>
              <p className="text-sm text-gray-600">{formData.active ? 'Paciente ativo no sistema' : 'Paciente inativo'}</p>
            </div>
            <button onClick={() => setFormData(prev => ({ ...prev, active: !prev.active }))} className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium ${formData.active ? 'bg-green-100 text-green-800 hover:bg-green-200' : 'bg-red-100 text-red-800 hover:bg-red-200'}`}>
              {formData.active ? <><ToggleRight className="w-5 h-5" />Ativo</> : <><ToggleLeft className="w-5 h-5" />Inativo</>}
            </button>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Valor da Sessão Individual (R$)</label>
            <input type="number" value={formData.individualSessionPrice || 0} onChange={(e) => setFormData(prev => ({ ...prev, individualSessionPrice: parseFloat(e.target.value) || 0 }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" placeholder="150.00" />
          </div>

          <div className="flex items-center justify-between p-4 bg-purple-50 rounded-lg">
            <div>
              <h3 className="font-medium">Pacote Contratado</h3>
              <p className="text-sm text-gray-600">{formData.hasPackage ? 'Paciente possui pacote ativo' : 'Sessões avulsas'}</p>
            </div>
            <button onClick={() => setFormData(prev => ({ ...prev, hasPackage: !prev.hasPackage }))} disabled={!isNewPackage} className={'flex items-center gap-2 px-4 py-2 rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed ' + (formData.hasPackage ? 'bg-purple-100 text-purple-800 hover:bg-purple-200' : 'bg-gray-100 text-gray-800 hover:bg-gray-200')}>
              <Package className="w-5 h-5" />
              {formData.hasPackage ? 'Com Pacote' : 'Sem Pacote'}
            </button>
          </div>

          {formData.hasPackage && (
            <div className="space-y-4 p-4 border border-purple-200 rounded-lg">
              <div className="flex justify-between items-center">
                <h3 className="font-medium text-purple-800 flex items-center gap-2"><Package className="w-5 h-5" />Detalhes do Pacote</h3>
                {!isNewPackage && (<span className="text-sm font-semibold bg-yellow-100 text-yellow-800 px-3 py-1 rounded-full">Somente Leitura</span>)}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Nome do Pacote</label>
                <input type="text" value={packageInfo.packageName || ''} disabled={!isNewPackage} onChange={(e) => setFormData(prev => ({ ...prev, packageInfo: { ...packageInfo, packageName: e.target.value } }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 disabled:bg-gray-100 disabled:cursor-not-allowed" placeholder="Ex: Pacote Profissional" />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Número de Sessões Contratadas</label>
                  <input
                    type="number"
                    min="1"
                    step="1"
                    value={packageInfo.packageSessionsContracted}
                    disabled={!isNewPackage}
                    onChange={(e) => {
                      const rawValue = Number(e.target.value);
                      const newCount = Number.isFinite(rawValue) && rawValue > 0 ? Math.floor(rawValue) : 1;
                      const usedSessions = packageInfo.packageSessionsUsed || 0;
                      setFormData(prev => ({
                        ...prev,
                        packageInfo: {
                          ...packageInfo,
                          packageSessionsContracted: newCount,
                          packageSessionsRemaining: Math.max(newCount - usedSessions, 0),
                        },
                      }));
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                    placeholder="Ex: 16"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Valor do Pacote (R$)</label>
                  <input type="number" value={packageInfo.packagePrice} disabled={!isNewPackage} onChange={(e) => setFormData(prev => ({ ...prev, packageInfo: { ...packageInfo, packagePrice: parseFloat(e.target.value) || 0 } }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 disabled:bg-gray-100 disabled:cursor-not-allowed" placeholder="1200.00" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-2">Sessões Utilizadas</label>
                  <div className="w-full px-3 py-2 bg-gray-100 border border-gray-200 rounded-lg text-center">{packageInfo.packageSessionsUsed}</div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-2">Sessões Restantes</label>
                  <div className="w-full px-3 py-2 bg-gray-100 border border-gray-200 rounded-lg text-center font-bold">{packageInfo.packageSessionsRemaining}</div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Validade do Pacote</label>
                <select value={packageInfo.packageValidityType} disabled={!isNewPackage} onChange={(e) => {
                    const newValidityType = e.target.value as ValidityType;
                    setFormData(prev => {
                      const updatedPackageInfo = { ...packageInfo, packageValidityType: newValidityType };
                      if (newValidityType === 'CUSTOM') updatedPackageInfo.packageCustomValidityMonths = 1;
                      else delete updatedPackageInfo.packageCustomValidityMonths;
                      return { ...prev, packageInfo: updatedPackageInfo };
                    });
                  }} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 disabled:bg-gray-100 disabled:cursor-not-allowed">
                  <option value="1_MONTH">1 mês</option>
                  <option value="2_MONTHS">2 meses</option>
                  <option value="3_MONTHS">3 meses</option>
                  <option value="CUSTOM">Personalizado</option>
                </select>
              </div>

              {packageInfo.packageValidityType === 'CUSTOM' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Duração (em meses)</label>
                  <input type="number" step="0.5" min="0.5" disabled={!isNewPackage} value={packageInfo.packageCustomValidityMonths || 1} onChange={(e) => setFormData(prev => ({ ...prev, packageInfo: { ...packageInfo, packageCustomValidityMonths: parseFloat(e.target.value) || 1 } }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 disabled:bg-gray-100 disabled:cursor-not-allowed" placeholder="Ex: 1.5 para um mês e meio" />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Data do Pagamento</label>
                <input type="date" disabled={!isNewPackage} value={packageInfo.packagePaymentDate} onChange={(e) => setFormData(prev => ({ ...prev, packageInfo: { ...packageInfo, packagePaymentDate: e.target.value } }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 disabled:bg-gray-100 disabled:cursor-not-allowed" />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Data de Renovação (calculada)</label>
                <input type="date" value={calculateRenewalDate() || ''} readOnly className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50" />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Observações</label>
                <textarea value={packageInfo.packageNotes || ''} onChange={(e) => setFormData(prev => ({ ...prev, packageInfo: { ...packageInfo, packageNotes: e.target.value } }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500" rows={3} placeholder="Observações sobre o pacote..." />
              </div>

              {/* Seção para Adicionar Créditos em Pacotes Existentes */}
              {!isNewPackage && (
                <div className="mt-6 pt-4 border-t border-dashed border-purple-300">
                  <h4 className="font-medium text-purple-800 mb-3">Gerenciar Pacote</h4>
                  {!showAddCredits ? (
                    <button 
                      onClick={() => setShowAddCredits(true)} 
                      className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                    >
                      <PlusCircle className="w-5 h-5" />
                      Adicionar Sessões (Crédito)
                    </button>
                  ) : (
                    <div className="p-4 bg-green-50 border border-green-200 rounded-lg space-y-4">
                      <h5 className="font-semibold">Adicionar Crédito de Sessões</h5>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Sessões a Adicionar</label>
                          <input 
                            type="number" 
                            value={creditsForm.sessions}
                            onChange={(e) => setCreditsForm(prev => ({ ...prev, sessions: parseInt(e.target.value) || 1 }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                            min="1"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Valor Total (R$)</label>
                          <input 
                            type="number" 
                            value={creditsForm.price}
                            onChange={(e) => setCreditsForm(prev => ({ ...prev, price: parseFloat(e.target.value) || 0 }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                            placeholder="Ex: 200.00"
                          />
                        </div>
                      </div>
                      <div className="flex items-center justify-end gap-3">
                        <button onClick={() => setShowAddCredits(false)} className="text-sm text-gray-600 hover:text-gray-800">Cancelar</button>
                        <button 
                          onClick={handleAddCredits} 
                          disabled={isAddingCredits}
                          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
                        >
                          {isAddingCredits ? 'Adicionando...' : 'Confirmar Adição'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 p-6 border-t bg-gray-50">
          <button onClick={onClose} className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50">
            {isNewPackage ? 'Cancelar' : 'Fechar'}
          </button>
          <button onClick={handleSave} disabled={(!isNewPackage && formData.hasPackage) || loading} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2">
            <Save className="w-4 h-4" />
            {loading ? 'Salvando...' : 'Salvar'}
          </button>
        </div>
      </div>
    </div>
  );
}
